import React, { useEffect, useState, useContext } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Alert,
  Tabs,
  Tab,
  useTheme,
  SvgIcon,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import {
  School as SchoolIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { trainingClient, TrainingAssignmentDto, TrainingCertificateDto, TrainingAssignmentStatus } from '../../../app/services/api/compliance/training';
import { UserContext } from '../../../components/shared/useUser';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { Section } from '../../../components/ui/Section';
import useLogError from '../../../hooks/useLogError';

const getStatusLabel = (status: string | number, t: (key: string) => string): string => {
  // Handle numeric enum values
  const statusNum = typeof status === 'number' ? status : parseInt(status);
  if (!isNaN(statusNum)) {
    switch (statusNum) {
      case 0: return t('statusLabels.assigned');
      case 1: return t('statusLabels.inProgress');
      case 2: return t('statusLabels.completed');
      case 3: return t('statusLabels.failed');
      default: return `${t('statusLabels.unknown')} (${status})`;
    }
  }
  // Handle string values (backwards compatibility)
  switch (status) {
    case 'Assigned': return t('statusLabels.assigned');
    case 'InProgress': return t('statusLabels.inProgress');
    case 'Completed': return t('statusLabels.completed');
    case 'Failed': return t('statusLabels.failed');
    default: return String(status);
  }
};

const getStatusColor = (status: string | number): 'default' | 'primary' | 'error' | 'info' | 'success' | 'warning' => {
  // Handle numeric enum values
  const statusNum = typeof status === 'number' ? status : parseInt(status);
  if (!isNaN(statusNum)) {
    switch (statusNum) {
      case 0: return 'warning';  // Assigned
      case 1: return 'info';     // InProgress
      case 2: return 'success';  // Completed
      case 3: return 'error';    // Failed
      default: return 'default';
    }
  }
  // Handle string values (backwards compatibility)
  switch (status) {
    case 'Assigned': return 'warning';
    case 'InProgress': return 'info';
    case 'Completed': return 'success';
    case 'Failed': return 'error';
    default: return 'default';
  }
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel"
      hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
};

const localeLanguage =
  typeof navigator !== 'undefined'
    ? (navigator.languages?.[0] || navigator.language)
    : undefined;

const MyTrainingPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation('training');
  const { user } = useContext(UserContext);
  const { logError } = useLogError();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assignments, setAssignments] = useState<TrainingAssignmentDto[]>([]);
  const [certificates, setCertificates] = useState<TrainingCertificateDto[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const userId = user?.id ?? 0;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    if (tabParam === 'certificates') {
      setTabValue(1);
    }

    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!userId) {
        setError(t('myTraining.userNotAuthenticated'));
        setLoading(false);
        return;
      }

      // Load assignments and certificates in parallel
      const [assignmentsData, certificatesData] = await Promise.all([
        trainingClient.getCompletionsByUserId(userId),
        trainingClient.getMyCertificates(),
      ]);

      setAssignments(assignmentsData);
      setCertificates(certificatesData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('myTraining.failedToLoadTrainingData');
      setError(errorMessage);
      logError(err);
    } finally {
      setLoading(false);
    }
  };
  const handleDownloadCertificate = async (certificate: TrainingCertificateDto) => {
    try {
      const blob = await trainingClient.downloadCertificate(certificate.id);

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Certificate_${certificate.certificateNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('myTraining.failedToDownloadCertificate');
      setError(errorMessage);
      logError(err);
    }
  };

  const handlePreviewCertificate = async (certificate: TrainingCertificateDto) => {
    try {
      const blob = await trainingClient.downloadCertificate(certificate.id);

      // Open in new tab for preview
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');

      // Clean up after a delay to ensure the PDF loads
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('myTraining.failedToPreviewCertificate');
      setError(errorMessage);
      logError(err);
    }
  };

  const handleStartTraining = async (assignment: TrainingAssignmentDto) => {
    try {
      // If training hasn't been started yet (status is Assigned), call startTraining
      if (isStatus(assignment.status, TrainingAssignmentStatus.Assigned)) {
        await trainingClient.startTraining({
          assignmentId: assignment.id,
          startedAt: new Date().toISOString(),
        });
      }

      // Navigate to training page
      navigate(`/compliance/training/modules/${assignment.moduleId}/take`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('myTraining.failedToStartTraining');
      setError(errorMessage);
      logError(err);
    }
  };

  const isStatus = (status: string | number, target: TrainingAssignmentStatus) => {
    return typeof status === 'number' ? status === target : parseInt(status) === target;
  };

  const pendingAssignments = assignments.filter(a => isStatus(a.status, TrainingAssignmentStatus.Assigned));
  const inProgressAssignments = assignments.filter(a => isStatus(a.status, TrainingAssignmentStatus.InProgress));
  const completedAssignments = assignments.filter(a => isStatus(a.status, TrainingAssignmentStatus.Completed));
  const failedAssignments = assignments.filter(a => isStatus(a.status, TrainingAssignmentStatus.Failed));
  const isBetweenLG = useMediaQuery('(min-width:1200px) and (max-width:1700px)');
  const isBetweenMD = useMediaQuery('(min-width:900px) and (max-width:1199px)');

  if (loading) {
    return (
      <PageArea>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 8,
          }}
        >
          <CircularProgress />
        </Box>
      </PageArea>
    );
  }


  return (
    <PageArea>
      <PageTitle subTitle={t('myTraining.subtitle')}>
        {t('myTraining.title')}
      </PageTitle>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid
        container
        spacing={5}
        sx={{ mt: 4, mb: 5 }}
      >
        <Grid
          item
          xs={12}
          sm={6}
          md={4}
          lg={3}
          xl={2}
        >
          <Section style={{ p: 5, mb: 5 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box flex={1}>
                <Box
                  sx={{
                    color: theme.palette.custom.gray[600],
                    fontSize: '10px',
                    textTransform: 'uppercase',
                    mb: 0.25,
                    fontWeight: 500,
                  }}
                >
                  {t('myTraining.pendingTraining')}
                </Box>
                <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                  {pendingAssignments.length}
                </Box>
                <Box
                  sx={{
                    fontSize: '12px',
                    color: theme.palette.custom.gray[600],
                  }}
                >
                  {t('myTraining.notStartedYet')}
                </Box>
              </Box>
              <Box
                sx={{
                  backgroundColor: '#ed6c0220',
                  borderRadius: '50%',
                  width: 48,
                  height: 48,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ed6c02',
                }}
              >
                <SvgIcon fontSize="medium">
                  <ScheduleIcon />
                </SvgIcon>
              </Box>
            </Box>
          </Section>
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          md={4}
          lg={3}
          xl={2}
        >
          <Section style={{ p: 5, mb: 5 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box flex={1}>
                <Box
                  sx={{
                    color: theme.palette.custom.gray[600],
                    fontSize: '10px',
                    textTransform: 'uppercase',
                    mb: 0.25,
                    fontWeight: 500,
                  }}
                >
                  {t('myTraining.inProgress')}
                </Box>
                <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                  {inProgressAssignments.length}
                </Box>
                <Box
                  sx={{
                    fontSize: '12px',
                    color: theme.palette.custom.gray[600],
                  }}
                >
                  {t('myTraining.currentlyLearning')}
                </Box>
              </Box>
              <Box
                sx={{
                  backgroundColor: '#1976d220',
                  borderRadius: '50%',
                  width: 48,
                  height: 48,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#1976d2',
                }}
              >
                <SvgIcon fontSize="medium">
                  <SchoolIcon />
                </SvgIcon>
              </Box>
            </Box>
          </Section>
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          md={4}
          lg={3}
          xl={2}
        >
          <Section style={{ p: 5, mb: 5 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box flex={1}>
                <Box
                  sx={{
                    color: theme.palette.custom.gray[600],
                    fontSize: '10px',
                    textTransform: 'uppercase',
                    mb: 0.25,
                    fontWeight: 500,
                  }}
                >
                  {t('myTraining.completed')}
                </Box>
                <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                  {completedAssignments.length}
                </Box>
                <Box
                  sx={{
                    fontSize: '12px',
                    color: theme.palette.custom.gray[600],
                  }}
                >
                  {t('myTraining.trainingFinished')}
                </Box>
              </Box>
              <Box
                sx={{
                  backgroundColor: '#2e7d3220',
                  borderRadius: '50%',
                  width: 48,
                  height: 48,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#2e7d32',
                }}
              >
                <SvgIcon fontSize="medium">
                  <CheckCircleIcon />
                </SvgIcon>
              </Box>
            </Box>
          </Section>
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          md={4}
          lg={3}
          xl={2}
        >
          <Section style={{ p: 5, mb: 5 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box flex={1}>
                <Box
                  sx={{
                    color: theme.palette.custom.gray[600],
                    fontSize: '10px',
                    textTransform: 'uppercase',
                    mb: 0.25,
                    fontWeight: 500,
                  }}
                >
                  {t('myTraining.failed')}
                </Box>
                <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                  {failedAssignments.length}
                </Box>
                <Box
                  sx={{
                    fontSize: '12px',
                    color: theme.palette.custom.gray[600],
                  }}
                >
                  {t('myTraining.needsRetry')}
                </Box>
              </Box>
              <Box
                sx={{
                  backgroundColor: '#d32f2f20',
                  borderRadius: '50%',
                  width: 48,
                  height: 48,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#d32f2f',
                }}
              >
                <SvgIcon fontSize="medium">
                  <CancelIcon />
                </SvgIcon>
              </Box>
            </Box>
          </Section>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Tabs
        value={tabValue}
        onChange={(e, newValue) => setTabValue(newValue)}
        sx={{ borderBottom: 1, borderColor: 'divider', px: 2, ml: 4, mr: 4, mb: '-1px' }}
      >
        <Tab label={t('myTraining.assignedTrainingTab')} />
        <Tab label={`${t('myTraining.certificatesTab')} (${certificates.length})`} />
      </Tabs>
      <Section>

        {/* Assigned Training Tab */}
        <TabPanel value={tabValue}
          index={0}>
          <Box sx={{ padding: 8 }}>
            <Grid
              container
              spacing={4}
            >
              {assignments.length === 0 ? (
                <Grid
                  item
                  xs={12}
                >
                  <Box
                    sx={{
                      py: 3,
                      color: theme.palette.custom.gray[600],
                      textAlign: 'center',
                    }}
                  >
                    {t('myTraining.noAssignments')}
                  </Box>
                </Grid>
              ) : (
                (() => {
                  // Sort assignments by status priority: Failed => In Progress => Assigned => Completed
                  const getStatusPriority = (status: string | number) => {
                    const statusNum = typeof status === 'number' ? status : Number.parseInt(status);
                    switch (statusNum) {
                      case TrainingAssignmentStatus.Failed: return 0; 
                      case TrainingAssignmentStatus.InProgress: return 1; 
                      case TrainingAssignmentStatus.Assigned: return 2; 
                      case TrainingAssignmentStatus.Completed: return 3; 
                      default: return 4;
                    }
                  };

                  const sortedAssignments = [...assignments].sort((a, b) =>
                    getStatusPriority(a.status) - getStatusPriority(b.status),
                  );

                  return sortedAssignments.map((assignment) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={isBetweenMD? 6 : 3}
                      lg={isBetweenLG ? 4 : 2}
                      key={assignment.id}
                    >
                      <Card sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        border: '1px solid',
                        boxShadow: 'none',
                        borderRadius: '12px',
                        borderColor: theme.palette.custom.gray[300],
                      }}>
                        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2.5 }}>
                          <Box
                            display="flex"
                            flexDirection={{ xs: 'column', sm: 'row' }}
                            justifyContent="space-between"
                            alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
                            mb={2}
                            gap={2}
                          >
                            <Box flex={1}>
                              <Box
                                sx={{
                                  fontSize: '1.1rem',
                                  fontWeight: 600,
                                  mb: 1,
                                  color: theme.palette.text.primary,
                                }}
                              >
                                {assignment.moduleName}
                              </Box>
                              <Chip
                                label={getStatusLabel(assignment.status, t)}
                                color={getStatusColor(assignment.status)}
                                size="small"
                                sx={{ fontWeight: 500 }}
                              />
                            </Box>

                            {!isStatus(assignment.status, TrainingAssignmentStatus.Completed) && !isStatus(assignment.status, TrainingAssignmentStatus.Failed) && (
                              <Button
                                variant="primary"
                                size="md"
                                onClick={() => handleStartTraining(assignment)}
                              >
                                {isStatus(assignment.status, TrainingAssignmentStatus.InProgress) ? t('myTraining.continue') : t('myTraining.start')}
                              </Button>
                            )}
                            {isStatus(assignment.status, TrainingAssignmentStatus.Failed) && (
                              <Tooltip
                                title={ t('myTraining.retryTooltip') }
                                arrow
                              >
                                <span>
                                  <Button
                                    variant="primary"
                                    size="md"
                                    onClick={() => handleStartTraining(assignment)}
                                  >
                                    {t('myTraining.retry')}
                                  </Button>
                                </span>
                              </Tooltip>
                            )}
                          </Box>

                          <Box
                            display="flex"
                            flexDirection={{ xs: 'column', sm: 'row' }}
                            justifyContent="space-between"
                            alignItems={{ xs: 'flex-start', sm: 'flex-end' }}
                            gap={{ xs: 2, sm: 0 }}
                            mt="auto"
                          >
                            <Box flex={1}>
                              <Box
                                sx={{
                                  fontSize: '0.875rem',
                                  color: theme.palette.custom.gray[700],
                                  mb: 0.5,
                                }}
                              >
                                <Box component="span"
                                  sx={{ fontWeight: 500 }}>{t('myTraining.assigned')}:</Box>{' '}
                                {assignment.assignedDate ? new Date(assignment.assignedDate).toLocaleDateString(localeLanguage) : '-'}
                              </Box>
                              {!isStatus(assignment.status, TrainingAssignmentStatus.Completed) && (
                                <Box
                                  sx={{
                                    fontSize: '0.875rem',
                                    color: isStatus(assignment.status, TrainingAssignmentStatus.Assigned) ? 'error.main' : theme.palette.custom.gray[700],
                                    mb: 0.5,
                                    fontWeight: isStatus(assignment.status, TrainingAssignmentStatus.Assigned) ? 600 : 400,
                                  }}
                                >
                                  <Box component="span"
                                    sx={{ fontWeight: 500 }}>{t('myTraining.due')}:</Box>{' '}
                                  {assignment.dueDate ? new Date(assignment.dueDate).toLocaleDateString(localeLanguage) : '-'}
                                </Box>
                              )}
                              {assignment.completedDate && (
                                <Box
                                  sx={{
                                    fontSize: '0.875rem',
                                    color: 'success.main',
                                    fontWeight: 500,
                                  }}
                                >
                                  <Box component="span">{t('myTraining.completedDate')}:</Box>{' '}
                                  {assignment.completedDate ? new Date(assignment.completedDate).toLocaleDateString(localeLanguage) : '-'}
                                </Box>
                              )}
                            </Box>

                            {/* Progress/Score as Circular Progress - Bottom Right */}
                            {(() => {
                              // For In Progress: show progress based on steps
                              if (isStatus(assignment.status, TrainingAssignmentStatus.InProgress)) {
                                const progress = assignment.totalSteps > 0
                                  ? (assignment.currentStep / assignment.totalSteps) * 100
                                  : 0;
                                return (
                                  <Box
                                    sx={{
                                      position: 'relative',
                                      display: 'inline-flex',
                                    }}
                                  >
                                    <CircularProgress
                                      variant="determinate"
                                      value={progress}
                                      size={60}
                                      thickness={4}
                                      sx={{
                                        color: theme.palette.info.main,
                                      }}
                                    />
                                    <Box
                                      sx={{
                                        top: 0,
                                        left: 0,
                                        bottom: 0,
                                        right: 0,
                                        position: 'absolute',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          fontSize: '0.875rem',
                                          fontWeight: 600,
                                          color: theme.palette.info.main,
                                        }}
                                      >
                                        {Math.round(progress)}%
                                      </Box>
                                    </Box>
                                  </Box>
                                );
                              }

                              // For Failed or Completed: show score if available
                              if ((isStatus(assignment.status, TrainingAssignmentStatus.Completed) || isStatus(assignment.status, TrainingAssignmentStatus.Failed)) &&
                                assignment.score !== null && assignment.score !== undefined) {
                                const color = isStatus(assignment.status, TrainingAssignmentStatus.Completed)
                                  ? theme.palette.success.main
                                  : theme.palette.error.main;

                                return (
                                  <Box
                                    sx={{
                                      position: 'relative',
                                      display: 'inline-flex',
                                    }}
                                  >
                                    <CircularProgress
                                      variant="determinate"
                                      value={assignment.score}
                                      size={60}
                                      thickness={4}
                                      sx={{
                                        color: color,
                                      }}
                                    />
                                    <Box
                                      sx={{
                                        top: 0,
                                        left: 0,
                                        bottom: 0,
                                        right: 0,
                                        position: 'absolute',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          fontSize: '0.875rem',
                                          fontWeight: 600,
                                          color: color,
                                        }}
                                      >
                                        {Math.round(assignment.score)}%
                                      </Box>
                                    </Box>
                                  </Box>
                                );
                              }

                              return null;
                            })()}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ));
                })()
              )}
            </Grid>
          </Box>
        </TabPanel>

        {/* My Certificates Tab */}
        <TabPanel value={tabValue}
          index={1}>
          <Box sx={{ padding: 8 }}>
            <Grid
              container
              spacing={5}
            >
              {certificates.length === 0 ? (
                <Grid
                  item
                  xs={12}
                >
                  <Box
                    sx={{
                      py: 3,
                      color: theme.palette.custom.gray[600],
                      textAlign: 'center',
                    }}
                  >
                    {t('myTraining.noCertificates')}
                  </Box>
                </Grid>
              ) : (
                certificates.map((cert) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    lg={2}
                    key={cert.id}
                  >
                    <Card sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      border: '1px solid',
                      boxShadow: 'none',
                      borderRadius: '12px',
                      borderColor: theme.palette.custom.gray[300],
                    }}>
                      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2.5 }}>
                        <Box sx={{ fontSize: '1.1rem', fontWeight: 600, mb: 3 }}>
                          {cert.moduleName}
                        </Box>

                        <Box mb={3}>
                          <Box
                            sx={{
                              fontSize: '0.875rem',
                              color: theme.palette.custom.gray[700],
                              mb: 1,
                            }}
                          >
                            <Box
                              component="span"
                              fontWeight={500}
                            >
                              {t('myTraining.certificateNumber')}:
                            </Box>{' '}
                            {cert.certificateNumber}
                          </Box>
                          <Box
                            sx={{
                              fontSize: '0.875rem',
                              color: theme.palette.custom.gray[700],
                              mb: 0.5,
                            }}
                          >
                            <Box
                              component="span"
                              fontWeight={500}
                            >
                              {t('myTraining.issued')}:
                            </Box>{' '}
                            {cert.issueDate ? new Date(cert.issueDate).toLocaleDateString(localeLanguage) : '-'}
                          </Box>
                          <Box
                            sx={{
                              fontSize: '0.875rem',
                              color: cert.isRevoked ? 'error.main' : theme.palette.custom.gray[700],
                            }}
                          >
                            <Box
                              component="span"
                              fontWeight={500}
                            >
                              {t('myTraining.validUntil')}:
                            </Box>{' '}
                            {cert.validUntil ? new Date(cert.validUntil).toLocaleDateString(localeLanguage) : t('myTraining.noExpiration')}
                          </Box>
                        </Box>

                        {cert.isRevoked ? (
                          <Box
                            sx={{
                              textAlign: 'center',
                              py: 1,
                              px: 2,
                              backgroundColor: 'error.light',
                              borderRadius: 1,
                              color: 'error.dark',
                              fontWeight: 500,
                              fontSize: '0.875rem',
                              mt: 'auto',
                            }}
                          >
                            {t('myTraining.certificateRevoked')}
                            {cert.revokedReason && (
                              <Box sx={{ fontSize: '0.75rem', mt: 0.5 }}>
                                {t('myTraining.revokedReason')}: {cert.revokedReason}
                              </Box>
                            )}
                          </Box>
                        ) : (
                          <Box sx={{ mt: 'auto', display: 'flex', gap: 2 }}>
                            <Button
                              variant="primary"
                              size="md"
                              startIcon={<SvgIcon><VisibilityIcon /></SvgIcon>}
                              fullWidth
                              onClick={() => handlePreviewCertificate(cert)}
                            >
                              {t('myTraining.preview')}
                            </Button>
                            <Button
                              variant="secondary"
                              size="md"
                              startIcon={<SvgIcon><DownloadIcon /></SvgIcon>}
                              fullWidth
                              onClick={() => handleDownloadCertificate(cert)}
                            >
                              {t('myTraining.download')}
                            </Button>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))
              )}
            </Grid>
          </Box>
        </TabPanel>
      </Section>
    </PageArea >
  );
};

export default MyTrainingPage;
